import * as React from 'react'

import {
  CreateTimesheetOvertimeRequestDocument,
  CreateTimesheetOvertimeRequestMutationVariables,
  TimesheetOvertimeRequestDocument,
  TimesheetOvertimeRequestFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'
import app from 'modules/core/store/models/app'

import OvertimeRequestForm from 'modules/attendance/components/ClockIn/Modals/OvertimeRequest/Form'

import ModalFooter from './ModalFooter'

interface Props {
  employeeId: string
  hoursAmount?: number
  onClose: () => void
  date?: string
}

const NewOvertimeRequest = ({ employeeId, hoursAmount, onClose, date }: Props) => {
  const { access: currentUser } = useCurrentSession()
  const formDate = dateToString(moment(date), 'backend') || dateToString(moment(), 'backend')
  const defaultValues: CreateTimesheetOvertimeRequestMutationVariables = {
    date: formDate,
    employeeId: employeeId,
    authorId: currentUser?.id,
    hoursAmount: hoursAmount,
    description: '',
  }

  const [createOvertimeRequest, { loading: creating }] = useMutation(
    CreateTimesheetOvertimeRequestDocument,
    {
      extractorFn: (data) => data.attendanceMutations.createAttendanceOvertimeRequest,
      refetchQueries: [TimesheetOvertimeRequestDocument],
    }
  )

  // TODO: fix this
  const { value: canApprove } = useAsyncCan('attendance.approve_overtime_request')

  const [overtimeRequest, setOvertimeRequest] = React.useState<
    TimesheetOvertimeRequestFragment | null | undefined
  >(null)
  const [loading, setLoading] = React.useState<boolean>(false)

  return (
    <FormProvider
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<CreateTimesheetOvertimeRequestMutationVariables> = {}

        if (!data.hoursAmount || (data.hoursAmount && data.hoursAmount <= 0)) {
          errors.hoursAmount = i18n.t('common.empty')
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        const result = await createOvertimeRequest({
          variables: {
            employeeId: data.employeeId,
            authorId: currentUser?.id,
            date: data.date,
            hoursAmount: data.hoursAmount,
            description: data.description,
          },
        })

        const handled = handleResult(result, (errors) => {
          setErrors({
            employeeId: errors.employeeId,
            authorId: errors.authorId,
            date: errors.date,
            hoursAmount: errors.hoursAmount,
            description: errors.description,
          })

          app.setFlash({
            type: 'wrong',
            message: errors?.messages || i18n.t('errors.generic_error'),
          })
        })

        if (handled.success && handled.data.overtimeRequest) {
          dispatch({ type: EventTypes.ATTENDANCE_SHIFT_TOGGLED })
          onClose()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      }}
    >
      <Modal
        illustration="timer"
        title={i18n.t('time_tracking.modals.overtime_request.title')}
        description={i18n.t('time_tracking.modals.overtime_request.description')}
        onClose={onClose}
        footer={
          <ModalFooter
            loading={creating || loading}
            onClose={onClose}
            overtimeRequest={overtimeRequest}
            canApprove={canApprove}
          />
        }
      >
        <OvertimeRequestForm
          canApprove={canApprove}
          isPending
          employeeId={employeeId}
          setLoading={setLoading}
          setOvertimeRequest={setOvertimeRequest}
        />
      </Modal>
    </FormProvider>
  )
}

export default observer(NewOvertimeRequest)
